import { useState, useEffect, useRef } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { ChevronLeft, CheckCircle, AlertCircle, ArrowRight, Info, Play, Square, RotateCcw, X, Calculator, Timer } from 'lucide-react';
import SiteHeaderV2 from '@/components/website/SiteHeaderV2';
import SiteFooterV2 from '@/components/website/SiteFooterV2';
import PageHeaderV2 from '@/components/website/PageHeaderV2';

/* eslint-disable react/no-unescaped-entities */

const EvaluationPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  
  // Nijmegen questionnaire items
  const nijmegenItems = [
    "Douleur thoracique",
    "Sensation de tension",
    "Vision trouble",
    "Vertiges/étourdissements",
    "Confusion/perte de contact avec la réalité",
    "Respiration plus rapide et plus profonde",
    "Essoufflement",
    "Oppression thoracique",
    "Ventre gonflé",
    "Fourmillements dans les doigts",
    "Difficultés à respirer profondément",
    "Raideur des doigts ou des bras",
    "Oppression autour de la bouche",
    "Mains et pieds froids",
    "Palpitations cardiaques",
    "Anxiété"
  ];

  const [nijmegenScores, setNijmegenScores] = useState(Array(16).fill(0));
  const [boltScore, setBoltScore] = useState('');
  const [respiratoryRate, setRespiratoryRate] = useState('');
  
  // Calculator Modal state
  const [showCalculatorModal, setShowCalculatorModal] = useState(false);
  const [breathsPerMinute, setBreathsPerMinute] = useState(6);
  
  // Chronometer state
  const [chronometerTime, setChronometerTime] = useState(0);
  const [isChronometerRunning, setIsChronometerRunning] = useState(false);
  const [chronometerFinished, setChronometerFinished] = useState(false);
  const chronometerIntervalRef = useRef(null);

  // Chronometer effect
  useEffect(() => {
    if (isChronometerRunning && chronometerTime < 60) {
      chronometerIntervalRef.current = setInterval(() => {
        setChronometerTime(prev => {
          if (prev >= 59) {
            setIsChronometerRunning(false);
            setChronometerFinished(true);
            clearInterval(chronometerIntervalRef.current);
            return 60;
          }
          return prev + 1;
        });
      }, 1000);
    }
    return () => {
      if (chronometerIntervalRef.current) {
        clearInterval(chronometerIntervalRef.current);
      }
    };
  }, [isChronometerRunning, chronometerTime]);

  const startChronometer = () => {
    setChronometerTime(0);
    setChronometerFinished(false);
    setIsChronometerRunning(true);
  };

  const stopChronometer = () => {
    setIsChronometerRunning(false);
    if (chronometerIntervalRef.current) {
      clearInterval(chronometerIntervalRef.current);
    }
  };

  const resetChronometer = () => {
    setIsChronometerRunning(false);
    setChronometerTime(0);
    setChronometerFinished(false);
    if (chronometerIntervalRef.current) {
      clearInterval(chronometerIntervalRef.current);
    }
  };

  const calculateCycleTime = (bpm) => {
    return 60 / bpm;
  };

  const getFrequencyStatus = (bpm) => {
    if (bpm >= 4 && bpm <= 5) return { label: "Niveau Yogi, respiration très lente", color: "text-slate-600 bg-sky-50" };
    if (bpm >= 6 && bpm <= 8) return { label: "Optimal (Cohérence Cardiaque)", color: "text-sky-600 bg-sky-50" };
    if (bpm >= 9 && bpm <= 12) return { label: "Très Bon", color: "text-sky-600 bg-sky-50" };
    if (bpm >= 13 && bpm <= 14) return { label: "Normal", color: "text-sky-600 bg-sky-50" };
    if (bpm >= 15 && bpm <= 20) return { label: "Élevé", color: "text-slate-600 bg-sky-50" };
    return { label: "Hyperventilation", color: "text-sky-600 bg-rose-50" };
  };

  const cycleTime = calculateCycleTime(breathsPerMinute);
  const frequencyStatus = getFrequencyStatus(breathsPerMinute);

  const closeCalculatorModal = () => {
    setShowCalculatorModal(false);
    resetChronometer();
  };

  // BOLT Test Modal state
  const [showBoltModal, setShowBoltModal] = useState(false);
  const [boltTestActive, setBoltTestActive] = useState(false);
  const [boltTime, setBoltTime] = useState(0);
  const [boltBestTime, setBoltBestTime] = useState(0);
  const [boltPrep, setBoltPrep] = useState(null); // 'inhale', 'exhale', 'pause', 'apnee'
  const [boltPrepProgress, setBoltPrepProgress] = useState(0);
  const boltIntervalRef = useRef(null);
  const boltPrepIntervalRef = useRef(null);

  // BOLT preparation effect
  useEffect(() => {
    if (!boltTestActive || !boltPrep) return;
    if (boltPrep === 'apnee') return;

    const phaseDuration = boltPrep === 'inhale' ? 2000 : boltPrep === 'exhale' ? 3000 : boltPrep === 'pause' ? 1000 : 0;
    
    if (phaseDuration === 0) return;

    const startTime = Date.now();
    boltPrepIntervalRef.current = setInterval(() => {
      const elapsed = Date.now() - startTime;
      const progress = Math.min(elapsed / phaseDuration, 1);
      setBoltPrepProgress(progress);
      
      if (progress >= 1) {
        clearInterval(boltPrepIntervalRef.current);
        if (boltPrep === 'inhale') {
          setBoltPrep('exhale');
          setBoltPrepProgress(0);
        } else if (boltPrep === 'exhale') {
          setBoltPrep('pause');
          setBoltPrepProgress(0);
        } else if (boltPrep === 'pause') {
          setBoltPrep('apnee');
          setBoltPrepProgress(0);
        }
      }
    }, 50);

    return () => {
      if (boltPrepIntervalRef.current) {
        clearInterval(boltPrepIntervalRef.current);
      }
    };
  }, [boltTestActive, boltPrep]);

  // BOLT timer effect (only during apnea)
  useEffect(() => {
    if (!boltTestActive || boltPrep !== 'apnee') return;

    boltIntervalRef.current = setInterval(() => {
      setBoltTime(prev => prev + 0.1);
    }, 100);

    return () => {
      if (boltIntervalRef.current) {
        clearInterval(boltIntervalRef.current);
      }
    };
  }, [boltTestActive, boltPrep]);

  const startBoltTest = () => {
    setBoltTime(0);
    setBoltPrep('inhale');
    setBoltPrepProgress(0);
    setBoltTestActive(true);
  };

  const stopBoltTest = () => {
    setBoltTestActive(false);
    if (boltIntervalRef.current) {
      clearInterval(boltIntervalRef.current);
    }
    if (boltPrepIntervalRef.current) {
      clearInterval(boltPrepIntervalRef.current);
    }
    // Update best time
    if (boltTime > boltBestTime) {
      setBoltBestTime(boltTime);
    }
    setBoltPrep(null);
  };

  const resetBoltTest = () => {
    setBoltTestActive(false);
    setBoltTime(0);
    setBoltPrep(null);
    setBoltPrepProgress(0);
    if (boltIntervalRef.current) {
      clearInterval(boltIntervalRef.current);
    }
    if (boltPrepIntervalRef.current) {
      clearInterval(boltPrepIntervalRef.current);
    }
  };

  const closeBoltModal = () => {
    setShowBoltModal(false);
    resetBoltTest();
  };

  // Open BOLT modal if URL parameter is present
  useEffect(() => {
    const test = searchParams.get('test');
    if (test === 'bolt') {
      setShowBoltModal(true);
    }
  }, [searchParams]);

  const handleNijmegenChange = (index, value) => {
    const newScores = [...nijmegenScores];
    newScores[index] = parseInt(value);
    setNijmegenScores(newScores);
  };

  const calculateNijmegenTotal = () => {
    return nijmegenScores.reduce((sum, score) => sum + score, 0);
  };

  const nijmegenTotal = calculateNijmegenTotal();
  const nijmegenPositive = nijmegenTotal >= 23;

  const getBoltInterpretation = (score) => {
    const boltValue = parseFloat(score);
    if (isNaN(boltValue)) return null;
    
    if (boltValue >= 40) return { level: "Excellent", color: "text-sky-600", bgColor: "bg-sky-50" };
    if (boltValue >= 30) return { level: "Bon", color: "text-sky-600", bgColor: "bg-sky-50" };
    if (boltValue >= 20) return { level: "Moyen", color: "text-slate-600", bgColor: "bg-sky-50" };
    if (boltValue >= 10) return { level: "Faible", color: "text-slate-600", bgColor: "bg-sky-50" };
    return { level: "Très faible", color: "text-sky-600", bgColor: "bg-slate-50" };
  };

  const getRateInterpretation = (rate) => {
    const rateValue = parseFloat(rate);
    if (isNaN(rateValue)) return null;
    
    if (rateValue <= 12) return { level: "Normal", color: "text-sky-600", bgColor: "bg-sky-50" };
    if (rateValue <= 16) return { level: "Légèrement élevé", color: "text-slate-600", bgColor: "bg-sky-50" };
    return { level: "Élevé (hyperventilation)", color: "text-sky-600", bgColor: "bg-slate-50" };
  };

  const boltInterpretation = getBoltInterpretation(boltScore);
  const rateInterpretation = getRateInterpretation(respiratoryRate);

  return (
    <div 
      className="min-h-screen"
      style={{
        background: 'linear-gradient(180deg, #FFFFFF 0%, #F0F7FC 55%, #D6EBF6 100%)',
        fontFamily: "Figtree, sans-serif",
      }}
    >
      <SiteHeaderV2 />
      
      {/* Page Header Image */}
      <PageHeaderV2 
        title="Évaluer Votre Respiration" 
        subtitle="Outils pour identifier les dysfonctionnements respiratoires"
      />
      
      {/* Hero */}
      <section className="py-12">
        <div className="max-w-5xl mx-auto px-4 sm:px-6">
          <p className="text-xl text-slate-600 leading-relaxed">
            Des outils simples pour évaluer votre pattern respiratoire et identifier d'éventuels dysfonctionnements.
          </p>
        </div>
      </section>

      {/* Respiratory Mechanics */}
      <section className="py-16">
        <div className="max-w-5xl mx-auto px-4 sm:px-6">
          <h2 className="text-3xl sm:text-4xl mb-6" style={{ fontFamily: 'Outfit, sans-serif', fontWeight: 300 }}>
            1. Évaluer Votre Mécanique Respiratoire
          </h2>
          
          <Card className="border-0 shadow-lg mb-6">
            <CardContent className="p-8">
              <h3 className="text-xl font-medium mb-4">Test d'Auto-Observation</h3>
              <p className="text-slate-600 mb-6">
                Asseyez-vous confortablement et observez votre respiration naturelle pendant 2-3 minutes sans la modifier.
              </p>
              
              <div className="space-y-4">
                <div className="flex items-start gap-3 p-4 rounded-lg">
                  <CheckCircle className="w-5 h-5 text-sky-500 mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="font-semibold text-slate-800">Respiration nasale ou buccale ?</p>
                    <p className="text-sm text-slate-600">La respiration devrait se faire exclusivement par le nez au repos</p>
                  </div>
                </div>
                
                <div className="flex items-start gap-3 p-4 rounded-lg">
                  <CheckCircle className="w-5 h-5 text-sky-500 mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="font-semibold text-slate-800">Respiration thoracique ou abdominale ?</p>
                    <p className="text-sm text-slate-600">
                      Lorsque le corps inspire, sentez-vous un relâchement doux du ventre, et une sensation d&apos;expansion douce, lente, progressive, à 360°, de votre région abdominale et thoracique ?
                      L&apos;expiration devrait être un simple relâchement, comme un soupir lent, agréable, et complet.
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-3 p-4 rounded-lg">
                  <CheckCircle className="w-5 h-5 text-sky-500 mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="font-semibold text-slate-800">Les épaules ?</p>
                    <p className="text-sm text-slate-600">
                      Est-ce que les épaules se soulèvent à l&apos;inspiration ? Est-ce que vous voyez et sentez les muscles du cou se contracter ? Ce sont des signes d&apos;un corps en situation de stress…
                    </p>
                  </div>
                </div>
                
                <div className="flex items-start gap-3 p-4 rounded-lg">
                  <CheckCircle className="w-5 h-5 text-sky-500 mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="font-semibold text-slate-800">Présence de soupirs ou bâillements fréquents ?</p>
                    <p className="text-sm text-slate-600">Signe possible d'hyperventilation chronique</p>
                  </div>
                </div>
                
                <div className="flex items-start gap-3 p-4 rounded-lg">
                  <CheckCircle className="w-5 h-5 text-sky-500 mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="font-semibold text-slate-800">Pause post-expiratoire</p>
                    <p className="text-sm text-slate-600">Il devrait y avoir une pause naturelle après l'expiration, avant la prochaine inspiration</p>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* Respiratory Rate */}
      <section className="py-16">
        <div className="max-w-5xl mx-auto px-4 sm:px-6">
          <h2 className="text-3xl sm:text-4xl mb-6" style={{ fontFamily: 'Outfit, sans-serif', fontWeight: 300 }}>
            2. Calculer Votre Fréquence Respiratoire
          </h2>
          
          <Card className="border-0 shadow-lg">
            <CardContent className="p-8">
              <div className="flex items-start gap-3 mb-6 p-4 bg-sky-50 rounded-lg">
                <Info className="w-5 h-5 text-sky-600 mt-0.5 flex-shrink-0" />
                <div>
                  <p className="text-sm text-slate-700">
                    <strong>Méthode :</strong> Comptez le nombre de respirations complètes (inspiration + expiration) pendant 1 minute, 
                    au repos, assis confortablement. Fréquence normale : 8-12 respirations/minute.
                  </p>
                </div>
              </div>

              {/* Button to open calculator modal */}
              <div className="mb-6">
                <button
                  onClick={() => setShowCalculatorModal(true)}
                  className="flex items-center gap-3 w-full px-6 py-4 rounded-3xl hover:from-teal-600 hover:to-cyan-600 text-white rounded-xl font-semibold transition-all shadow-lg hover:shadow-xl"
                >
                  <Calculator size={24} />
                  <div className="text-left">
                    <p className="text-lg">Ouvrir le Calculateur de Fréquence Respiratoire</p>
                    <p className="text-sm text-slate-600 font-normal">Avec chronomètre intégré de 60 secondes</p>
                  </div>
                </button>
              </div>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    Votre fréquence respiratoire (respirations/minute)
                  </label>
                  <input
                    type="number"
                    value={respiratoryRate}
                    onChange={(e) => setRespiratoryRate(e.target.value)}
                    placeholder="Ex: 14"
                    className="w-full px-4 py-3 border border-slate-200 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                  />
                </div>

                {rateInterpretation && (
                  <div className={`p-4 rounded-lg ${rateInterpretation.bgColor}`}>
                    <p className={`font-bold ${rateInterpretation.color}`}>
                      Interprétation : {rateInterpretation.level}
                    </p>
                    {parseFloat(respiratoryRate) > 16 && (
                      <p className="text-sm text-slate-700 mt-2">
                        Une fréquence supérieure à 16/min au repos peut indiquer une hyperventilation chronique.
                      </p>
                    )}
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* BOLT Test */}
      <section className="py-16">
        <div className="max-w-5xl mx-auto px-4 sm:px-6">
          <h2 className="text-3xl sm:text-4xl mb-2" style={{ fontFamily: 'Outfit, sans-serif', fontWeight: 300 }}>
            3. Test BOLT (Body Oxygen Level Test)
          </h2>
          <p className="text-slate-600 mb-6">
            Mesure de votre sensibilité à la montée du CO<sub>2</sub> dans le sang.
          </p>
          
          <Card className="border-0 shadow-lg">
            <CardContent className="p-8">
              <div className="flex items-start gap-3 mb-6 p-4 bg-sky-50 rounded-lg">
                <Info className="w-5 h-5 text-sky-600 mt-0.5 flex-shrink-0" />
                <div>
                  <p className="text-sm text-slate-700 mb-3">
                    <strong>Méthode :</strong>
                  </p>
                  <ol className="text-sm text-slate-700 space-y-2 list-decimal list-inside">
                    <li>Assis confortablement, respirez normalement par le nez pendant quelques minutes</li>
                    <li>Après une expiration normale, pincez votre nez et démarrez un chronomètre</li>
                    <li>Retenez votre souffle jusqu'au <strong>premier besoin de respirer</strong> (pas jusqu'au maximum !)</li>
                    <li>Notez le temps en secondes</li>
                  </ol>
                </div>
              </div>

              {/* Button to open BOLT test modal */}
              <div className="mb-6">
                <button
                  onClick={() => setShowBoltModal(true)}
                  className="flex items-center gap-3 w-full px-6 py-4 bg-gradient-to-r from-violet-500 to-purple-500 hover:from-violet-600 hover:to-purple-600 text-white rounded-xl font-semibold transition-all shadow-lg hover:shadow-xl"
                >
                  <Timer size={24} />
                  <div className="text-left">
                    <p className="text-lg">Lancer le Défi BOLT Interactif</p>
                    <p className="text-sm text-violet-100 font-normal">Avec guide respiratoire et chronomètre automatique</p>
                  </div>
                </button>
              </div>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    Votre score BOLT (secondes)
                  </label>
                  <input
                    type="number"
                    value={boltScore}
                    onChange={(e) => setBoltScore(e.target.value)}
                    placeholder="Ex: 25"
                    className="w-full px-4 py-3 border border-slate-200 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                  />
                </div>

                {boltInterpretation && (
                  <div className={`p-4 rounded-lg ${boltInterpretation.bgColor}`}>
                    <p className={`font-bold ${boltInterpretation.color} mb-2`}>
                      Score : {boltInterpretation.level}
                    </p>
                    <div className="text-sm text-slate-700 space-y-1">
                      <p>• {'<'} 10s : Tolérance au CO2 très faible, hyperventilation sévère</p>
                      <p>• 10-20s : Tolérance faible, hyperventilation modérée</p>
                      <p>• 20-30s : Tolérance moyenne, légère hyperventilation</p>
                      <p>• 30-40s : Bonne tolérance, respiration efficace</p>
                      <p>• {'>'} 40s : Excellente tolérance, respiration optimale</p>
                    </div>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* Nijmegen Questionnaire */}
      <section className="py-16">
        <div className="max-w-5xl mx-auto px-4 sm:px-6">
          <h2 className="text-3xl sm:text-4xl mb-6" style={{ fontFamily: 'Outfit, sans-serif', fontWeight: 300 }}>
            4. Questionnaire de Nijmegen
          </h2>
          
          <Card className="border-0 shadow-lg mb-6">
            <CardContent className="p-8">
              <div className="flex items-start gap-3 mb-6 p-4 bg-sky-50 rounded-lg">
                <Info className="w-5 h-5 text-sky-600 mt-0.5 flex-shrink-0" />
                <p className="text-sm text-slate-700">
                  <strong>Instructions :</strong> Pour chaque symptôme, indiquez à quelle fréquence vous l'avez ressenti au cours des derniers temps.
                  Score positif si ≥ 23/64 (sensibilité 91%, spécificité 95%)
                </p>
              </div>

              <div className="overflow-x-auto">
                <table className="w-full border-collapse">
                  <thead>
                    <tr className="bg-slate-100">
                      <th className="px-4 py-3 text-left text-sm font-semibold text-slate-700 border">Symptôme</th>
                      <th className="px-3 py-3 text-center text-sm font-semibold text-slate-700 border">Jamais (0)</th>
                      <th className="px-3 py-3 text-center text-sm font-semibold text-slate-700 border">Rarement (1)</th>
                      <th className="px-3 py-3 text-center text-sm font-semibold text-slate-700 border">Parfois (2)</th>
                      <th className="px-3 py-3 text-center text-sm font-semibold text-slate-700 border">Souvent (3)</th>
                      <th className="px-3 py-3 text-center text-sm font-semibold text-slate-700 border">Très souvent (4)</th>
                    </tr>
                  </thead>
                  <tbody>
                    {nijmegenItems.map((item, index) => (
                      <tr key={index} className={index % 2 === 0 ? 'bg-white' : 'bg-slate-50'}>
                        <td className="px-4 py-3 text-sm text-slate-700 border">{item}</td>
                        {[0, 1, 2, 3, 4].map((value) => (
                          <td key={value} className="px-3 py-3 text-center border">
                            <input
                              type="radio"
                              name={`nijmegen-${index}`}
                              value={value}
                              checked={nijmegenScores[index] === value}
                              onChange={() => handleNijmegenChange(index, value)}
                              className="w-4 h-4 text-sky-600 focus:ring-teal-500 cursor-pointer"
                            />
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <div className="mt-6 p-6 bg-gradient-to-r from-slate-100 to-slate-50 rounded-lg">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-lg font-medium">Score Total :</span>
                  <span className="text-3xl font-bold text-sky-600">{nijmegenTotal} / 64</span>
                </div>
                
                {nijmegenTotal > 0 && (
                  <div className={`mt-4 p-4 rounded-lg ${nijmegenPositive ? 'bg-slate-50' : 'bg-sky-50'}`}>
                    <div className="flex items-start gap-3">
                      {nijmegenPositive ? (
                        <>
                          <AlertCircle className="w-5 h-5 text-sky-600 mt-0.5 flex-shrink-0" />
                          <div>
                            <p className="font-bold text-sky-600">Score positif (≥ 23)</p>
                            <p className="text-sm text-slate-700 mt-1">
                              Votre score suggère un syndrome d'hyperventilation. Une consultation avec un professionnel 
                              de santé et une rééducation respiratoire sont recommandées.
                            </p>
                          </div>
                        </>
                      ) : (
                        <>
                          <CheckCircle className="w-5 h-5 text-sky-600 mt-0.5 flex-shrink-0" />
                          <div>
                            <p className="font-bold text-sky-600">Score négatif ({'<'} 23)</p>
                            <p className="text-sm text-slate-700 mt-1">
                              Votre score ne suggère pas de syndrome d'hyperventilation selon ce questionnaire.
                            </p>
                          </div>
                        </>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>

          <div className="rounded-2xl p-6 shadow-lg">
            <p className="text-sm text-slate-600 mb-4">
              <strong>Note importante :</strong> Ces tests sont des outils d'évaluation et ne remplacent pas un diagnostic médical. 
              Si vous présentez des symptômes respiratoires persistants, consultez un professionnel de santé.
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <Button onClick={() => navigate('/documents?open=oa-manual')} className="bg-sky-500 hover:bg-emerald-600 rounded-full w-full sm:w-auto">
                Commencer la rééducation
                <ArrowRight className="ml-2" size={18} />
              </Button>
              <Button onClick={() => navigate('/app-landing')} variant="outline" className="rounded-full w-full sm:w-auto border-sky-500 text-sky-600 hover:bg-cyan-50">
                Accéder aux exercices
                <ArrowRight className="ml-2" size={18} />
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Calculator Modal */}
      {showCalculatorModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
          <div className="rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            {/* Modal Header */}
            <div className="flex items-center justify-between p-6 border-b border-slate-200">
              <h2 className="text-2xl font-medium" style={{ fontFamily: 'Outfit, sans-serif', fontWeight: 300 }}>
                Calculateur de Fréquence Respiratoire
              </h2>
              <button
                onClick={closeCalculatorModal}
                className="p-2 hover:bg-slate-100 rounded-full transition-colors"
              >
                <X size={24} className="text-slate-600" />
              </button>
            </div>
            
            {/* Modal Content */}
            <div className="p-6 space-y-8">
              {/* Chronometer Section */}
              <div>
                <h3 className="text-xl font-medium mb-4 text-center">
                  🕐 Mesurez Votre Fréquence Respiratoire Actuelle
                </h3>
                
                <div className="p-4 bg-sky-50 border-l-4 border-sky-500 rounded-lg mb-6">
                  <p className="text-slate-700 leading-relaxed text-sm">
                    <strong>Instructions :</strong> Posez une main à cheval sur le ventre et les dernières côtes et, 
                    au lancement du chronomètre, comptez le nombre de respirations jusqu'à l'arrêt du chronomètre, 
                    afin de mesurer votre fréquence respiratoire actuelle.
                  </p>
                </div>

                <div className="flex flex-col items-center">
                  {/* Chronometer Display */}
                  <div className="relative mb-6">
                    <div 
                      className={`w-32 h-32 rounded-full flex items-center justify-center border-4 ${
                        isChronometerRunning 
                          ? 'border-sky-500 bg-sky-50' 
                          : chronometerFinished 
                          ? 'border-sky-500 bg-sky-50' 
                          : 'border-slate-300'
                      } transition-colors duration-300`}
                    >
                      <div className="text-center">
                        <div className={`text-4xl font-bold ${
                          isChronometerRunning 
                            ? 'text-sky-600' 
                            : chronometerFinished 
                            ? 'text-sky-600' 
                            : 'text-slate-600'
                        }`}>
                          {chronometerTime}
                        </div>
                        <div className="text-xs text-slate-500">secondes</div>
                      </div>
                    </div>
                    
                    {/* Progress Ring */}
                    <svg 
                      className="absolute inset-0 w-32 h-32 -rotate-90"
                      viewBox="0 0 128 128"
                    >
                      <circle
                        cx="64"
                        cy="64"
                        r="60"
                        fill="none"
                        stroke={isChronometerRunning ? "#14b8a6" : chronometerFinished ? "#22c55e" : "#e2e8f0"}
                        strokeWidth="4"
                        strokeDasharray={`${(chronometerTime / 60) * 377} 377`}
                        className="transition-all duration-1000"
                      />
                    </svg>
                  </div>

                  {/* Controls */}
                  <div className="flex gap-3">
                    {!isChronometerRunning && !chronometerFinished && (
                      <button
                        onClick={startChronometer}
                        className="flex items-center gap-2 px-5 py-2.5 bg-cyan-500 hover:bg-cyan-600 text-white rounded-full font-semibold transition-colors shadow-lg"
                      >
                        <Play size={18} />
                        Démarrer
                      </button>
                    )}
                    
                    {isChronometerRunning && (
                      <button
                        onClick={stopChronometer}
                        className="flex items-center gap-2 px-5 py-2.5 bg-sky-500 hover:bg-orange-600 text-white rounded-full font-semibold transition-colors shadow-lg"
                      >
                        <Square size={18} />
                        Arrêter
                      </button>
                    )}
                    
                    {(chronometerFinished || (!isChronometerRunning && chronometerTime > 0)) && (
                      <button
                        onClick={resetChronometer}
                        className="flex items-center gap-2 px-5 py-2.50 hover:bg-slate-600 text-white rounded-full font-semibold transition-colors shadow-lg"
                      >
                        <RotateCcw size={18} />
                        Recommencer
                      </button>
                    )}
                  </div>

                  {chronometerFinished && (
                    <div className="mt-4 p-3 bg-sky-50 border-l-4 border-sky-500 rounded-lg">
                      <p className="text-sky-800 font-semibold text-sm"> Chronomètre terminé !</p>
                      <p className="text-sky-700 text-xs mt-1">
                        Notez le nombre de respirations comptées et utilisez le calculateur ci-dessous.
                      </p>
                    </div>
                  )}
                </div>
              </div>

              {/* Divider */}
              <div className="border-t border-slate-200"></div>

              {/* Calculator Section */}
              <div>
                <div className="text-center mb-4">
                  <p className="text-slate-600 text-sm mb-4">Ajustez le curseur pour voir l'impact de la fréquence respiratoire</p>
                  <div className="flex items-center justify-center gap-4">
                    <button
                      onClick={() => setBreathsPerMinute(Math.max(4, breathsPerMinute - 1))}
                      className="w-10 h-10 rounded-full bg-sky-100 hover:bg-teal-200 text-sky-600 font-bold"
                    >
                      -
                    </button>
                    <div className="text-center">
                      <div className="text-4xl font-bold text-sky-600">{breathsPerMinute}</div>
                      <div className="text-xs text-slate-500">respirations/minute</div>
                    </div>
                    <button
                      onClick={() => setBreathsPerMinute(Math.min(25, breathsPerMinute + 1))}
                      className="w-10 h-10 rounded-full bg-sky-100 hover:bg-teal-200 text-sky-600 font-bold"
                    >
                      +
                    </button>
                  </div>
                  
                  <div className="mt-4">
                    <input
                      type="range"
                      min="4"
                      max="25"
                      value={breathsPerMinute}
                      onChange={(e) => setBreathsPerMinute(parseInt(e.target.value))}
                      className="w-full max-w-sm"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4 mt-6">
                  <div className="p-3 rounded-xl">
                    <h4 className="font-semibold text-slate-700 text-sm mb-1">Durée d'un Cycle</h4>
                    <p className="text-2xl font-medium">{cycleTime.toFixed(1)}s</p>
                    <p className="text-xs text-slate-500">(Inspiration + Expiration)</p>
                  </div>
                  
                  <div className={`p-3 rounded-xl ${frequencyStatus.color}`}>
                    <h4 className="font-semibold text-sm mb-1">Évaluation</h4>
                    <p className="text-lg font-bold">{frequencyStatus.label}</p>
                    <p className="text-xs mt-1 opacity-80">
                      {breathsPerMinute >= 4 && breathsPerMinute <= 5
                        ? "Maîtrise avancée"
                        : breathsPerMinute >= 6 && breathsPerMinute <= 12 
                        ? "Fréquence optimale"
                        : breathsPerMinute >= 13 && breathsPerMinute <= 14
                        ? "Perfectible"
                        : "Rééducation conseillée"}
                    </p>
                  </div>
                </div>

                {(breathsPerMinute === 4 || breathsPerMinute === 5) && (
                  <div className="mt-4 p-3 bg-sky-50 border-l-4 border-sky-500 rounded-lg">
                    <p className="text-slate-800 font-semibold text-sm">🧘 Niveau Yogi !</p>
                    <p className="text-slate-700 text-xs mt-1">
                      Respiration très lente, typique des pratiquants avancés de yoga et méditation.
                    </p>
                  </div>
                )}

                {breathsPerMinute === 6 && (
                  <div className="mt-4 p-3 bg-sky-50 border-l-4 border-sky-500 rounded-lg">
                    <p className="text-sky-800 font-semibold text-sm"> Cohérence Cardiaque Parfaite !</p>
                    <p className="text-sky-700 text-xs mt-1">
                      6 respirations/min = fréquence idéale pour maximiser la variabilité cardiaque.
                    </p>
                  </div>
                )}
              </div>
            </div>

            {/* Modal Footer */}
            <div className="p-6 border-t border-slate-200">
              <button
                onClick={closeCalculatorModal}
                className="w-full px-6 py-3 bg-slate-700 hover:bg-slate-800 text-white rounded-xl font-semibold transition-colors"
              >
                Fermer
              </button>
            </div>
          </div>
        </div>
      )}

      {/* BOLT Test Modal */}
      {showBoltModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-3xl w-full max-h-[90vh] overflow-y-auto">
            {/* Modal Header */}
            <div className="flex items-center justify-between p-6 border-b border-slate-200">
              <h2 className="text-2xl font-medium" style={{ fontFamily: 'Outfit, sans-serif', fontWeight: 300 }}>
                 Défi BOLT - Test d'Apnée
              </h2>
              <button
                onClick={closeBoltModal}
                className="p-2 hover:bg-slate-100 rounded-full transition-colors"
              >
                <X size={24} className="text-slate-600" />
              </button>
            </div>
            
            {/* Modal Content */}
            <div className="p-8">
              {/* Instructions */}
              <div className="p-4 bg-violet-50 border-l-4 border-violet-500 rounded-lg mb-8">
                <p className="text-slate-700 text-sm leading-relaxed">
                  <strong>Instructions :</strong> Cliquez sur le cercle pour démarrer. Le test vous guidera avec une 
                  <span className="text-slate-600 font-semibold"> inspiration</span>, puis une 
                  <span className="text-sky-600 font-semibold"> expiration normale</span>, avant de commencer 
                  <span className="text-violet-600 font-semibold"> l'apnée</span>. Cliquez à nouveau au 
                  <strong className="text-sky-600"> premier besoin de respirer</strong> (pas au maximum !).
                </p>
              </div>

              <div className="flex flex-col md:flex-row items-center justify-center gap-8">
                {/* Main Circle */}
                <div className="flex flex-col items-center">
                  <div
                    data-testid="bolt-circle"
                    onClick={() => {
                      if (!boltTestActive) {
                        startBoltTest();
                      } else if (boltPrep === 'apnee') {
                        stopBoltTest();
                      }
                    }}
                    className={`relative w-48 h-48 rounded-full cursor-pointer transition-all duration-300 flex items-center justify-center ${
                      !boltTestActive 
                        ? 'bg-gradient-to-br from-violet-400 to-purple-500 hover:from-violet-500 hover:to-purple-600 shadow-lg hover:shadow-xl' 
                        : boltPrep === 'inhale'
                        ? 'bg-gradient-to-br from-amber-400 to-orange-400'
                        : boltPrep === 'exhale'
                        ? 'bg-gradient-to-br from-sky-400 to-indigo-400'
                        : boltPrep === 'pause'
                        ? 'bg-gradient-to-br from-slate-400 to-slate-500'
                        : 'bg-gradient-to-br from-sky-500 to-sky-600'
                    }`}
                    style={{
                      transform: `scale(${
                        boltPrep === 'inhale' 
                          ? 1 + boltPrepProgress * 0.3 
                          : boltPrep === 'exhale' 
                          ? 1.3 - boltPrepProgress * 0.3
                          : 1
                      })`,
                      transition: 'transform 0.1s linear'
                    }}
                  >
                    <div className="absolute inset-4 rounded-full bg-white/20 backdrop-blur-sm" />
                    <div className="relative z-10 text-center">
                      {!boltTestActive ? (
                        <div className="flex flex-col items-center">
                          <Play size={40} className="text-white mb-2" />
                          <p className="text-white font-semibold">Cliquez pour</p>
                          <p className="text-white font-semibold">commencer</p>
                        </div>
                      ) : boltPrep === 'inhale' ? (
                        <div className="flex flex-col items-center">
                          <p className="text-3xl font-bold text-white">Inspirez</p>
                          <p className="text-white/80 text-sm mt-1">par le nez</p>
                        </div>
                      ) : boltPrep === 'exhale' ? (
                        <div className="flex flex-col items-center">
                          <p className="text-3xl font-bold text-white">Expirez</p>
                          <p className="text-white/80 text-sm mt-1">normalement</p>
                        </div>
                      ) : boltPrep === 'pause' ? (
                        <div className="flex flex-col items-center">
                          <p className="text-3xl font-bold text-white">Prêt...</p>
                          <p className="text-white/80 text-sm mt-1">pincez le nez</p>
                        </div>
                      ) : (
                        <div className="flex flex-col items-center">
                          <p className="text-4xl font-bold text-white">{boltTime.toFixed(1)}s</p>
                          <p className="text-white/80 text-sm mt-1">Apnée en cours</p>
                          <p className="text-white/60 text-xs mt-2">Cliquez au 1er besoin</p>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Status */}
                  <div className="mt-4 text-center">
                    {boltTestActive && boltPrep === 'apnee' && (
                      <p className="text-sky-500 text-sm font-medium">
                         Cliquez dès le premier besoin de respirer !
                      </p>
                    )}
                  </div>
                </div>

                {/* Score Panel */}
                <div className="flex flex-col gap-4 min-w-[180px]">
                  {/* Current Time */}
                  <div className="bg-slate-100 rounded-xl p-4 text-center">
                    <p className="text-xs font-medium text-slate-500 uppercase tracking-wide mb-1">
                      Temps Actuel
                    </p>
                    <p className="text-4xl font-medium">
                      {boltTime.toFixed(1)}
                    </p>
                    <p className="text-xs text-slate-500">secondes</p>
                  </div>

                  {/* Best Score */}
                  <div className="bg-violet-50 rounded-xl p-4 text-center">
                    <p className="text-xs font-medium text-violet-600 uppercase tracking-wide mb-1">
                      Record Personnel
                    </p>
                    <p className="text-4xl font-bold text-violet-600">
                      {boltBestTime.toFixed(1)}
                    </p>
                    <p className="text-xs text-violet-500">secondes</p>
                  </div>

                  {/* Interpretation */}
                  {boltTime > 0 && !boltTestActive && (
                    <div className={`rounded-xl p-4 text-center ${
                      boltTime < 10 ? 'bg-sky-100' :
                      boltTime < 20 ? 'bg-sky-100' :
                      boltTime < 30 ? 'bg-sky-100' :
                      boltTime < 40 ? 'bg-sky-100' :
                      'bg-sky-100'
                    }`}>
                      <p className="text-xs font-medium text-slate-600 uppercase tracking-wide mb-1">
                        Évaluation
                      </p>
                      <p className={`text-lg font-bold ${
                        boltTime < 10 ? 'text-sky-600' :
                        boltTime < 20 ? 'text-slate-600' :
                        boltTime < 30 ? 'text-slate-600' :
                        boltTime < 40 ? 'text-sky-600' :
                        'text-sky-600'
                      }`}>
                        {boltTime < 10 ? 'Très faible' :
                         boltTime < 20 ? 'Faible' :
                         boltTime < 30 ? 'Moyen' :
                         boltTime < 40 ? 'Bon' :
                         'Excellent'}
                      </p>
                    </div>
                  )}

                  {/* Reset Button */}
                  {boltTime > 0 && !boltTestActive && (
                    <button
                      onClick={resetBoltTest}
                      className="flex items-center justify-center gap-2 px-4 py-2 bg-slate-200 hover:bg-slate-300 text-slate-700 rounded-lg font-medium transition-colors"
                    >
                      <RotateCcw size={16} />
                      Recommencer
                    </button>
                  )}
                </div>
              </div>

              {/* Score Guide */}
              <div className="mt-8 grid grid-cols-5 gap-2 text-center text-xs">
                <div className="p-2 bg-sky-100 rounded-lg">
                  <p className="font-bold text-sky-600">&lt;10s</p>
                  <p className="text-sky-500">Très faible</p>
                </div>
                <div className="p-2 bg-sky-100 rounded-lg">
                  <p className="font-bold text-slate-600">10-20s</p>
                  <p className="text-slate-500">Faible</p>
                </div>
                <div className="p-2 bg-sky-100 rounded-lg">
                  <p className="font-bold text-slate-600">20-30s</p>
                  <p className="text-slate-500">Moyen</p>
                </div>
                <div className="p-2 bg-sky-100 rounded-lg">
                  <p className="font-bold text-sky-600">30-40s</p>
                  <p className="text-sky-500">Bon</p>
                </div>
                <div className="p-2 bg-sky-100 rounded-lg">
                  <p className="font-bold text-sky-600">&gt;40s</p>
                  <p className="text-sky-500">Excellent</p>
                </div>
              </div>
            </div>

            {/* Modal Footer */}
            <div className="p-6 border-t border-slate-200">
              <button
                onClick={closeBoltModal}
                className="w-full px-6 py-3 bg-slate-700 hover:bg-slate-800 text-white rounded-xl font-semibold transition-colors"
              >
                Fermer
              </button>
            </div>
          </div>
        </div>
      )}

      <SiteFooterV2 />
    </div>
  );
};

export default EvaluationPage;
